import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { 
  FaArrowLeft, FaArrowRight, FaRocket, FaGlobe, FaCheckCircle, 
  FaLightbulb, FaHandsHelping, FaPlus, FaChevronRight, FaPlay, FaEye, FaUsers,
  FaGavel, FaBuilding, FaLaptop, FaHome, FaPlane, FaUserTie
} from 'react-icons/fa';
import { useLanguage } from '../component/LanguageProvider';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../component/SEO';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

// Import real project assets
import residencyImg from '../Images/residency_service.png';
import relocationImg from '../Images/relocation_service.png';

gsap.registerPlugin(ScrollTrigger);

export const AboutUsPage = () => {
  const { currentLang, isInitialized } = useLanguage();
  const navigate = useNavigate();
  const langKey = (currentLang || 'en').toLowerCase();
  const isAR = langKey === 'ar';
  const containerRef = useRef(null);
  const [scrollPct, setScrollPct] = useState(0);
  const [counterVisible, setCounterVisible] = useState(false);
  const [counterVal, setCounterVal] = useState(0);
  const counterRef = useRef(null);
  const planeRef2 = useRef(null);

  const translations = {
    en: {
      pageTitle: 'About FlyVia | Spain Residency & Relocation',
      backToHome: 'Back to Home',
      heroTitle: 'About Us',
      heroSubtitle: 'FlyVia provides practical pathways for residency, company formation, and European relocation.',
      breadcrumbHome: 'Home',
      breadcrumbCurrent: 'About Us',

      ourStoryLabel: 'Who We Are',
      storyTitle: 'Focusing on Practical Support',
      storyContent: 'Moving to Europe should be organized. We help individuals and business owners with services that connect residency, company formation, and settlement under one brand. No complexity, just clarity.',
      
      storyStats: [
        { value: 'Spain', label: 'Primary Focus', icon: <FaLightbulb /> },
        { value: 'Europe', label: 'Relocation', icon: <FaGlobe /> },
        { value: 'Practical', label: 'Guidance', icon: <FaCheckCircle /> },
        { value: 'Support', label: 'Client-First', icon: <FaHandsHelping /> }
      ],

      benefitsList: [
        'Professional Focus',
        'Expert Certified',
        'Practical Guidance',
        'Clarity in Process',
        'Trustworthy',
        'High Integrity',
        'Custom Solutions',
        'Global Reach'
      ],

      ourMission: 'Our Mission',
      missionContent: 'To make moving to Spain easier to understand through clear guidance and practical relocation services.',

      ourVision: 'Our Vision',
      visionContent: 'To be the most trusted partner for Spain residency by helping clients plan with confidence.',

      meetTeam: 'How We Help',
      teamDescription: 'Solutions built for real relocation needs, ensuring a smooth transition to Europe.',

      teamMembers: [
        { id: '01', name: 'Residency', role: 'LEGAL PATHWAYS', description: 'Choosing the right residency path and organizing documents correctly.', accent: '#ae0001', icon: <FaGavel /> },
        { id: '02', name: 'Business', role: 'COMPANY SETUP', description: 'Practical support for starting or expanding a business in Spain.', accent: '#2c3b5b', icon: <FaBuilding /> },
        { id: '03', name: 'Aviation', role: 'PRIVATE JET', description: 'Bespoke flight solutions for those who value time and privacy.', accent: '#ae0001', icon: <FaPlane /> },
        { id: '04', name: 'Nomads', role: 'REMOTE WORK', description: 'Support for freelancers moving to Spain under Nomad routes.', accent: '#2c3b5b', icon: <FaLaptop /> },
        { id: '05', name: 'Properties', role: 'LUXURY ESTATES', description: 'Exclusive access to premium properties in the Mediterranean.', accent: '#ae0001', icon: <FaHome /> },
        { id: '06', name: 'Concierge', role: 'VIP SERVICES', description: 'Lifestyle management and bespoke arrangements for the elite.', accent: '#2c3b5b', icon: <FaUserTie /> },
        { id: '07', name: 'Families', role: 'RELOCATION', description: 'Transition support for families moving to a new life in Europe.', accent: '#ae0001', icon: <FaHandsHelping /> }
      ],

      whyChooseUs: 'Why FlyVia?',
      benefits: [
        { title: 'Expert Focus', desc: 'Specialized guidance for Spain and European residency.', icon: <FaGlobe /> },
        { title: 'Real Cases', desc: 'Based on successful relocations and practical experience.', icon: <FaUsers /> },
        { title: 'Practical Execution', desc: 'We don\'t just advise, we help you execute every step.', icon: <FaRocket /> },
        { title: 'Full Support', desc: 'End-to-end assistance from documents to settlement.', icon: <FaHandsHelping /> },
        { title: 'Clear Planning', desc: 'No hidden complexities, just a roadmap to your future.', icon: <FaLightbulb /> },
        { title: 'Transparent Process', desc: 'Complete clarity in every stage of your transition.', icon: <FaEye /> }
      ],

      getInTouch: 'Ready to Move?',
      contactDescription: 'Consult with us about residency, relocation, or business setup.',
      startYourJourney: 'Start Your Journey',
    },
    ar: {
      pageTitle: 'عن FlyVia | الإقامة في إسبانيا والانتقال',
      backToHome: 'العودة للرئيسية',
      heroTitle: 'من نحن',
      heroSubtitle: 'تقدم FlyVia مسارات عملية للإقامة، وتأسيس الشركات، والانتقال إلى أوروبا.',
      breadcrumbHome: 'الرئيسية',
      breadcrumbCurrent: 'من نحن',

      ourStoryLabel: 'من نحن',
      storyTitle: 'التركيز على الدعم العملي',
      storyContent: 'الانتقال إلى أوروبا يجب أن يكون منظمًا. نحن نساعد الأفراد وأصحاب الأعمال بخدمات تربط بين الإقامة وتأسيس الشركات والاستقرار تحت علامة واحدة. لا تعقيد، فقط وضوح.',

      storyStats: [
        { value: 'إسبانيا', label: 'التركيز الأساسي', icon: <FaLightbulb /> },
        { value: 'أوروبا', label: 'الانتقال', icon: <FaGlobe /> },
        { value: 'عملي', label: 'توجيه', icon: <FaCheckCircle /> },
        { value: 'دعم', label: 'العميل أولاً', icon: <FaHandsHelping /> }
      ],

      benefitsList: [
        'تركيز احترافي',
        'خبراء معتمدون',
        'توجيه عملي',
        'وضوح في العملية',
        'جدير بالثقة',
        'نزاهة عالية',
        'حلول مخصصة',
        'انتشار عالمي'
      ],

      ourMission: 'مهمتنا',
      missionContent: 'تسهيل الانتقال إلى إسبانيا من خلال توجيه واضح وخدمات عملية.',

      ourVision: 'رؤيتنا',
      visionContent: 'أن نكون الشريك الأكثر ثقة للإقامة في إسبانيا من خلال مساعدة العملاء على التخطيط بثقة.',

      meetTeam: 'كيف نساعدك',
      teamDescription: 'حلول مصممة لاحتياجات حقيقية، تضمن انتقالاً سلساً إلى أوروبا.',

      teamMembers: [
        { id: '01', name: 'الإقامة', role: 'المسارات القانونية', description: 'اختيار مسار الإقامة المناسب وتنظيم المستندات بشكل صحيح.', accent: '#ae0001', icon: <FaGavel /> },
        { id: '02', name: 'البيزنس', role: 'تأسيس الشركات', description: 'دعم عملي لبدء أو توسيع نشاط تجاري في إسبانيا.', accent: '#2c3b5b', icon: <FaBuilding /> },
        { id: '03', name: 'الطيران', role: 'طيران خاص', description: 'حلول طيران مخصصة لأولئك الذين يقدرون الوقت والخصوصية.', accent: '#ae0001', icon: <FaPlane /> },
        { id: '04', name: 'الرحالة الرقميون', role: 'العمل عن بعد', description: 'دعم للفريلانسرز المنتقلين إلى إسبانيا عبر مسارات الـ Nomad.', accent: '#2c3b5b', icon: <FaLaptop /> },
        { id: '05', name: 'العقارات', role: 'عقارات فاخرة', description: 'وصول حصري للعقارات المتميزة في منطقة البحر المتوسط.', accent: '#ae0001', icon: <FaHome /> },
        { id: '06', name: 'الكونسيرج', role: 'خدمات VIP', description: 'إدارة أسلوب الحياة وترتيبات مخصصة للنخبة.', accent: '#2c3b5b', icon: <FaUserTie /> },
        { id: '07', name: 'العائلات', role: 'دعم الانتقال', description: 'دعم الانتقال للعائلات التي تبدأ حياة جديدة في أوروبا.', accent: '#ae0001', icon: <FaHandsHelping /> }
      ],

      whyChooseUs: 'لماذا FlyVia؟',
      benefits: [
        { title: 'تركيز متخصص', desc: 'توجيه متخصص للإقامة في إسبانيا وأوروبا.', icon: <FaGlobe /> },
        { title: 'حالات حقيقية', desc: 'بناءً على عمليات انتقال ناجحة وخبرة عملية.', icon: <FaUsers /> },
        { title: 'تنفيذ عملي', desc: 'نحن لا نقدم المشورة فقط، بل نساعدك في التنفيذ.', icon: <FaRocket /> },
        { title: 'دعم شامل', desc: 'مساعدة كاملة من المستندات حتى الاستقرار.', icon: <FaHandsHelping /> },
        { title: 'تخطيط واضح', desc: 'لا تعقيدات خفية، فقط خارطة طريق لمستقبلك.', icon: <FaLightbulb /> },
        { title: 'عملية شفافة', desc: 'وضوح تام في كل مرحلة من مراحل انتقالك.', icon: <FaEye /> }
      ],

      getInTouch: 'جاهز للانتقال؟',
      contactDescription: 'استشرنا بخصوص الإقامة، الانتقال، أو تأسيس الشركات.',
      startYourJourney: 'ابدأ رحلتك',
    }
  };

  const t = useCallback((key) => {
    return translations[langKey]?.[key] || translations.en[key] || key;
  }, [langKey, translations]);

  const safeArray = useCallback((key) => {
    const val = t(key);
    return Array.isArray(val) ? val : [];
  }, [t]);

  useEffect(() => {
    if (!isInitialized) return;
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.reveal-up').forEach((el) => {
        gsap.from(el, {
          y: 60, opacity: 0, duration: 1, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 85%' }
        });
      });
    }, containerRef);
    return () => ctx.revert();
  }, [isInitialized, currentLang]);

  /* Scroll progress */
  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      setScrollPct(Math.round((scrollTop / (scrollHeight - clientHeight)) * 100));
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Counter animation for trust badge */
  useEffect(() => {
    const el = counterRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !counterVisible) {
        setCounterVisible(true);
        let i = 0;
        const end = 100;
        const step = () => {
          i += 3;
          setCounterVal(Math.min(i, end));
          if (i < end) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [counterVisible]);

  /* Card tilt on How We Help */
  useEffect(() => {
    const cards = document.querySelectorAll('.slide-card');
    const handlers = [];
    cards.forEach(card => {
      const onMove = (e) => {
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 14;
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * 14;
        card.style.transform = `perspective(600px) rotateY(${x}deg) rotateX(${-y}deg) translateY(-6px)`;
      };
      const onLeave = () => { card.style.transform = ''; };
      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
      handlers.push({ card, onMove, onLeave });
    });
    return () => handlers.forEach(({ card, onMove, onLeave }) => {
      card.removeEventListener('mousemove', onMove);
      card.removeEventListener('mouseleave', onLeave);
    });
  }, [isInitialized]);

  if (!isInitialized) return null;

  return (
    <div className="about-custom-page" ref={containerRef} dir={isAR ? 'rtl' : 'ltr'}>
      <SEO 
        title={t('heroTitle')} 
        description={t('heroSubtitle')}
        keywords={isAR ? 'عن FlyVia, مهمتنا, رؤيتنا, خبراء الانتقال إلى أوروبا' : 'about FlyVia, our mission, our vision, European relocation experts'}
      />

      <style>{`
        .about-custom-page { font-family: var(--font-primary); background: #fff; overflow-x: hidden; }
        
        /* Banner Style */
        .page-banner {
          position: relative;
          height: 65vh;
          min-height: 500px;
          background: linear-gradient(rgba(7, 17, 31, 0.7), rgba(7, 17, 31, 0.7)), 
                      url('https://images.unsplash.com/photo-1542296332-2e4473faf563?auto=format&fit=crop&w=800&q=80');
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          color: #fff;
          clip-path: polygon(0 0, 100% 0, 100% 88%, 50% 100%, 0 88%);
          margin-bottom: 60px;
        }

        .banner-content h1 {
          font-size: clamp(45px, 7vw, 85px);
          font-weight: 900;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: -2px;
          line-height: 1;
        }

        .breadcrumbs {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          font-weight: 800;
          font-size: 15px;
          margin-top: 25px;
        }
        .breadcrumbs a { color: #fff; text-decoration: none; opacity: 0.7; transition: 0.3s; }
        .breadcrumbs a:hover { opacity: 1; }
        .breadcrumbs span { color: var(--primary-color); }

        /* Story Editorial Design */
        .section-padding { padding: 120px 0; }
        
        .story-visuals {
          position: relative;
          padding-right: 40px;
        }
        [dir="rtl"] .story-visuals { padding-right: 0; padding-left: 40px; }

        .story-img-main {
          width: 100%;
          height: 650px;
          object-fit: cover;
          border-radius: 12px;
          box-shadow: 30px 30px 60px rgba(0,0,0,0.1);
          position: relative;
          z-index: 2;
        }

        .story-offset-box {
          position: absolute;
          top: -40px;
          right: 0;
          width: 80%;
          height: 100%;
          background: #f1f5f9;
          border-radius: 12px;
          z-index: 1;
        }
        [dir="rtl"] .story-offset-box { right: auto; left: 0; }

        .trust-badge {
          position: absolute;
          bottom: 40px;
          left: -30px;
          background: var(--primary-color);
          color: #fff;
          padding: 30px;
          border-radius: 12px;
          z-index: 3;
          box-shadow: 0 20px 40px rgba(174, 0, 1, 0.3);
          text-align: center;
        }
        [dir="rtl"] .trust-badge { left: auto; right: -30px; }
        .trust-badge h4 { font-size: 36px; font-weight: 900; margin: 0; }
        .trust-badge p { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; margin: 0; opacity: 0.9; }

        .story-kicker {
          color: var(--primary-color);
          font-weight: 800;
          font-size: 14px;
          margin-bottom: 12px;
          display: block;
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        .story-h2 {
          font-size: clamp(38px, 5vw, 58px);
          font-weight: 900;
          color: #07111f;
          margin-bottom: 30px;
          line-height: 1.1;
          letter-spacing: -2px;
        }

        .stat-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          margin-top: 50px;
        }
        .stat-item {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .stat-icon {
          width: 60px;
          height: 60px;
          background: #f8fafc;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--primary-color);
          font-size: 24px;
          transition: 0.3s;
        }
        .stat-item:hover .stat-icon { background: var(--primary-color); color: #fff; transform: translateY(-5px); }
        .stat-text h5 { font-weight: 900; margin: 0; color: #07111f; }
        .stat-text p { font-size: 14px; margin: 0; color: #64748b; }

        /* Mission Vision Capsule Style */
        .mv-capsule-section { padding: 120px 0; background: #f8fafc; }
        .capsule-grid { display: flex; align-items: center; justify-content: center; gap: 0; }

        .mv-capsule-card {
          flex: 1;
          background: #07111f;
          color: #fff;
          padding: 60px 40px;
          min-height: 380px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
          z-index: 1;
          transition: 0.4s;
          overflow: hidden;
        }

        .mv-capsule-card::after {
          content: "";
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          opacity: 0.15;
          z-index: -1;
          transition: 0.6s;
        }

        .card-left::after { background-image: url(${residencyImg}); }
        .card-right::after { background-image: url(${relocationImg}); }

        .card-left { border-radius: 0 160px 160px 0; }
        .card-right { border-radius: 160px 0 0 160px; }
        [dir="rtl"] .card-left { border-radius: 160px 0 0 160px; }
        [dir="rtl"] .card-right { border-radius: 0 160px 160px 0; }

        .mv-capsule-card:hover { transform: scale(1.02); z-index: 3; box-shadow: 0 30px 60px rgba(0,0,0,0.2); }
        .mv-capsule-card:hover::after { opacity: 0.25; transform: scale(1.1); }

        .capsule-icon-badge {
          width: 80px;
          height: 80px;
          background: var(--primary-color);
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin-bottom: 25px;
          box-shadow: 0 10px 20px rgba(174, 0, 1, 0.3);
        }

        .mv-capsule-card h3 { font-weight: 900; font-size: 32px; margin-bottom: 20px; text-transform: uppercase; }
        .mv-capsule-card p { font-size: 16px; opacity: 0.8; line-height: 1.7; max-width: 320px; }

        .capsule-center-img {
          width: 380px;
          height: 520px;
          object-fit: cover;
          border-radius: 190px;
          z-index: 2;
          margin: 0 -60px;
          border: 10px solid #fff;
          box-shadow: 0 20px 50px rgba(0,0,0,0.15);
        }

        /* Why FlyVia Aviation Style */
        .why-section { background: #07111f; padding: 140px 0; position: relative; overflow: hidden; }
        .plane-center {
          position: relative;
          z-index: 2;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .plane-img {
          width: 100%;
          max-width: 500px;
          filter: drop-shadow(0 30px 50px rgba(0,0,0,0.5));
        }
        .plane-glow {
          position: absolute;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(174,0,1,0.1) 0%, transparent 70%);
          z-index: -1;
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 50%;
        }

        .benefit-node {
          margin-bottom: 60px;
          transition: 0.4s;
        }
        .node-left { text-align: right; }
        .node-right { text-align: left; }
        
        .node-header {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 12px;
        }
        .node-left .node-header { flex-direction: row-reverse; }

        .node-icon {
          width: 50px;
          height: 50px;
          background: var(--primary-color);
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          box-shadow: 0 10px 20px rgba(174,0,1,0.3);
        }
        .node-header h4 { font-weight: 900; color: var(--primary-color); margin: 0; font-size: 22px; }
        .node-desc { color: rgba(255,255,255,0.7); font-size: 15px; line-height: 1.6; max-width: 300px; }
        .node-left .node-desc { margin-left: auto; }

        /* How We Help Slide-Style Redesign */
        .help-slide-section { padding: 140px 0; background: #fff; }
        
        .slide-card {
          background: #fff;
          padding: 60px 35px;
          border-radius: 0 0 20px 20px;
          border: 1px solid #f1f5f9;
          position: relative;
          transition: 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
          height: 100%;
          box-shadow: 0 15px 35px rgba(0,0,0,0.03);
          display: flex;
          flex-direction: column;
        }
        .slide-card:hover { transform: translateY(-10px); box-shadow: 0 30px 60px rgba(0,0,0,0.08); border-color: #e2e8f0; }

        .slide-accent {
          position: absolute;
          top: -1px;
          left: -1px;
          width: 80px;
          height: 80px;
          background: var(--primary-color);
          clip-path: polygon(0 0, 100% 0, 0 100%);
          border-radius: 20px 0 0 0;
          z-index: 2;
        }
        [dir="rtl"] .slide-accent { left: auto; right: -1px; clip-path: polygon(0 0, 100% 0, 100% 100%); border-radius: 0 20px 0 0; }

        .slide-icon-wrapper {
          align-self: flex-end;
          font-size: 28px;
          color: var(--primary-color);
          margin-bottom: 20px;
          opacity: 0.8;
        }

        .slide-card h3 { 
          font-weight: 900; 
          font-size: 28px; 
          color: #07111f; 
          margin-bottom: 10px; 
          text-transform: lowercase; 
        }

        .slide-desc { 
          font-size: 15px; 
          color: #64748b; 
          line-height: 1.6; 
          margin-bottom: 40px; 
        }

        .slide-number {
          margin-top: auto;
          font-weight: 900;
          font-size: 42px;
          color: #f1f5f9;
          line-height: 1;
          transition: 0.3s;
        }
        .slide-card:hover .slide-number { color: var(--primary-color); opacity: 0.15; }

        @media (max-width: 1200px) {
          .capsule-center-img { width: 300px; height: 450px; margin: 0 -40px; }
          .mv-capsule-card { padding: 40px 30px; }
          .section-padding { padding: 80px 0; }
        }

        /* ===== LIFE ANIMATIONS ===== */
        @keyframes aboutFloat { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-20px) rotate(3deg)} }
        @keyframes aboutOrbit { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes aboutOrbitReverse { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
        @keyframes aboutGradientShift { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes aboutParticleFloat { 0%,100%{transform:translateY(0);opacity:.7} 50%{transform:translateY(-25px);opacity:.2} }
        @keyframes aboutPulseRing { 0%{transform:scale(1);opacity:.6} 100%{transform:scale(2.2);opacity:0} }
        @keyframes aboutLineGrow { from{height:0;opacity:0} to{height:100%;opacity:1} }
        @keyframes aboutCountIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes aboutScanLine { 0%{top:0} 100%{top:100%} }

        /* Scroll progress bar */
        .about-scroll-progress {
          position: fixed;
          top: 0; left: 0;
          height: 3px;
          background: linear-gradient(90deg,#ae0001,#ff4444);
          z-index: 9999;
          transition: width .1s linear;
          box-shadow: 0 0 8px rgba(174,0,1,.6);
        }

        /* Banner particles */
        .banner-particle {
          position: absolute;
          width: 4px; height: 4px;
          border-radius: 50%;
          background: rgba(174,0,1,.8);
          pointer-events: none;
          animation: aboutParticleFloat ease-in-out infinite;
        }
        .banner-particle::after {
          content:''; position:absolute; inset:-4px;
          border-radius:50%;
          background:rgba(174,0,1,.3);
          filter:blur(4px);
        }

        /* Banner scan line */
        .banner-scan {
          position: absolute;
          left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg,transparent,rgba(174,0,1,.4),transparent);
          animation: aboutScanLine 4s linear infinite;
          pointer-events: none;
          z-index: 3;
        }

        /* Animated gradient heading */
        .gradient-heading {
          background: linear-gradient(270deg,#07111f,#ae0001,#07111f,#2c3b5b);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: aboutGradientShift 6s ease infinite;
        }

        /* Trust badge live */
        .trust-badge-live {
          animation: aboutFloat 4s ease-in-out infinite;
        }

        /* Plane floating */
        .plane-img-animated {
          animation: aboutFloat 5s ease-in-out infinite;
          filter: drop-shadow(0 30px 50px rgba(0,0,0,.5)) drop-shadow(0 0 30px rgba(174,0,1,.2));
        }

        /* Orbit rings */
        .orbit-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(174,0,1,.15);
          pointer-events: none;
        }
        .orbit-ring-1 { width:300px;height:300px; animation:aboutOrbit 20s linear infinite; }
        .orbit-ring-2 { width:450px;height:450px; animation:aboutOrbitReverse 30s linear infinite; border-style:dashed; }
        .orbit-ring-3 { width:600px;height:600px; animation:aboutOrbit 45s linear infinite; border-color:rgba(174,0,1,.06); }

        .orbit-dot {
          position: absolute;
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #ae0001;
          box-shadow: 0 0 10px rgba(174,0,1,.8);
          top: -4px; left: 50%;
          transform: translateX(-50%);
        }

        /* Pulse ring on stat icons */
        .stat-icon-pulse {
          position: relative;
        }
        .stat-icon-pulse::after {
          content:'';
          position:absolute;
          inset:-4px;
          border-radius:14px;
          border:1.5px solid rgba(174,0,1,.4);
          animation:aboutPulseRing 2s ease-out infinite;
        }

        /* Animated border on mission cards */
        @keyframes aboutBorderRun { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
        .mv-capsule-card::before {
          content:'';
          position:absolute;
          inset:-2px;
          border-radius: inherit;
          background: linear-gradient(90deg,rgba(174,0,1,.6),rgba(255,255,255,.1),rgba(174,0,1,.6));
          background-size:200% 100%;
          animation:aboutBorderRun 3s linear infinite;
          z-index:-1;
        }

        /* Slide card number glow on hover */
        .slide-card:hover .slide-number {
          color: transparent !important;
          -webkit-text-stroke: 1px rgba(174,0,1,.3);
        }

        /* Kicker dot pulse */
        .kicker-dot {
          display:inline-block;
          width:6px;height:6px;
          border-radius:50%;
          background:#ae0001;
          margin-right:8px;
          animation:aboutPulseRing 1.5s ease-out infinite;
          vertical-align:middle;
        }

        @media (max-width: 992px) {
          .plane-center { order: -1; margin-bottom: 60px; }
          .node-left, .node-right { text-align: center; }
          .node-header, .node-left .node-header { flex-direction: column; }
          .node-left .node-desc, .node-right .node-desc { margin: 0 auto; }
          .capsule-grid { flex-direction: column; gap: 40px; }
          .capsule-center-img { width: 100%; height: 400px; border-radius: 30px; margin: 0; border: none; }
          .mv-capsule-card { width: 100%; border-radius: 30px !important; flex: none; min-height: auto; }
        }
      `}</style>

      {/* Scroll progress bar */}
      <div className="about-scroll-progress" style={{ width: `${scrollPct}%` }} />

      {/* Banner */}
      <section className="page-banner">
        <div className="banner-scan" />
        {[...Array(8)].map((_, i) => (
          <div key={i} className="banner-particle" style={{
            left: `${10 + i * 11}%`,
            top: `${20 + (i % 3) * 25}%`,
            animationDuration: `${2.5 + i * 0.4}s`,
            animationDelay: `${i * 0.3}s`,
            width: i % 2 === 0 ? '5px' : '3px',
            height: i % 2 === 0 ? '5px' : '3px',
          }} />
        ))}
        <Container className="banner-content" style={{ position: 'relative', zIndex: 4 }}>
          <h1 className="reveal-up">{t('heroTitle')}</h1>
          <div className="breadcrumbs reveal-up">
            <Link to="/">{t('breadcrumbHome')}</Link>
            {isAR ? <FaArrowLeft /> : <FaArrowRight />}
            <span>{t('breadcrumbCurrent')}</span>
          </div>
        </Container>
      </section>

      {/* Who We Are */}
      <section className="section-padding">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <div className="story-visuals reveal-up">
                <div className="story-offset-box"></div>
                <img src="/about_success.png" alt="Who We Are" className="story-img-main" />
                <div className="trust-badge trust-badge-live">
                  <h4 ref={counterRef}>{counterVisible ? counterVal + '+' : '0+'}</h4>
                  <p>{isAR ? 'انتقال ناجح' : 'Success Stories'}</p>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="ps-lg-5 reveal-up">
                <span className="story-kicker"><span className="kicker-dot" />{t('ourStoryLabel')}</span>
                <h2 className="story-h2 gradient-heading">{t('storyTitle')}</h2>
                <p className="m-desc fs-5 mb-8" style={{ color: '#475569' }}>{t('storyContent')}</p>
                
                <div className="stat-grid">
                  {safeArray('storyStats').map((stat, i) => (
                    <div key={i} className="stat-item">
                      <div className="stat-icon stat-icon-pulse">{stat.icon}</div>
                      <div className="stat-text">
                        <h5>{stat.value}</h5>
                        <p>{stat.label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button onClick={() => navigate('/#contact')} className="btn-lg px-10 py-2 mt-12 rounded-1 font-black shadow-lg border-0" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                  {isAR ? 'تواصل معنا الآن' : 'Consult With Us'}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why FlyVia Aviation Section */}
      <section className="why-section">
        <Container>
          <div className="text-center mb-24 reveal-up">
            <span className="story-kicker" style={{ color: '#fff', opacity: 0.6 }}><span className="kicker-dot" />{t('whyChooseUs')}</span>
            <h2 className="story-h2 text-white">{isAR ? 'لماذا تختار FlyVia؟' : 'Excellence in Relocation'}</h2>
          </div>
          
          <Row className="align-items-center gy-5">
            {/* Left Side */}
            <Col lg={4}>
              {safeArray('benefits').slice(0, 3).map((item, i) => (
                <div key={i} className="benefit-node node-left reveal-up">
                  <div className="node-header">
                    <div className="node-icon">{item.icon}</div>
                    <h4>{item.title}</h4>
                  </div>
                  <p className="node-desc">{item.desc}</p>
                </div>
              ))}
            </Col>

            {/* Center Plane */}
            <Col lg={4}>
              <div className="plane-center reveal-up" style={{ position: 'relative' }}>
                <div className="orbit-ring orbit-ring-1" style={{ position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)' }}>
                  <div className="orbit-dot" />
                </div>
                <div className="orbit-ring orbit-ring-2" style={{ position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)' }} />
                <div className="orbit-ring orbit-ring-3" style={{ position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)' }} />
                <div className="plane-glow"></div>
                <img src="/flyvia_jet-removebg-preview.png" alt="Corporate Jet" className="plane-img plane-img-animated" />
              </div>
            </Col>

            {/* Right Side */}
            <Col lg={4}>
              {safeArray('benefits').slice(3, 6).map((item, i) => (
                <div key={i} className="benefit-node node-right reveal-up">
                  <div className="node-header">
                    <div className="node-icon">{item.icon}</div>
                    <h4>{item.title}</h4>
                  </div>
                  <p className="node-desc">{item.desc}</p>
                </div>
              ))}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission & Vision - Capsule Design */}
      <section className="mv-capsule-section">
        <Container>
          <div className="text-center mb-16 reveal-up">
            <span className="story-kicker"><span className="kicker-dot" />{t('ourMission')} & {t('ourVision')}</span>
            <h2 className="story-h2 gradient-heading">{isAR ? 'قيمنا وأهدافنا' : 'Our Mission & Vision Statement'}</h2>
          </div>
          
          <div className="capsule-grid reveal-up">
            <div className="mv-capsule-card card-left">
               <div className="capsule-icon-badge"><FaGlobe /></div>
               <h3>{t('ourVision')}</h3>
               <p>{t('visionContent')}</p>
            </div>

            <img 
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80" 
              alt="Our Team" 
              className="capsule-center-img" 
            />

            <div className="mv-capsule-card card-right">
               <div className="capsule-icon-badge"><FaRocket /></div>
               <h3>{t('ourMission')}</h3>
               <p>{t('missionContent')}</p>
            </div>
          </div>
        </Container>
      </section>

      {/* How We Help - Slide Style Grid */}
      <section className="help-slide-section">
        <Container>
          <div className="text-center mb-16 reveal-up">
            <span className="story-kicker"><span className="kicker-dot" />{t('meetTeam')}</span>
            <h2 className="story-h2">{t('meetTeam')}</h2>
            <p className="m-desc mx-auto" style={{ maxWidth: '600px' }}>{t('teamDescription')}</p>
          </div>
          <Row className="gy-4">
            {safeArray('teamMembers').map((m, i) => (
              <Col lg={3} md={6} key={i}>
                <div className="slide-card reveal-up">
                  <div className="slide-accent" style={{ background: m.accent }}></div>
                  <div className="slide-icon-wrapper" style={{ color: m.accent }}>{m.icon}</div>
                  <div className="small text-uppercase fw-black mb-2" style={{ color: m.accent, fontSize: '12px', letterSpacing: '1px' }}>{m.role}</div>
                  <h3>{m.name}</h3>
                  <p className="slide-desc">{m.description}</p>
                  <div className="slide-number">{m.id}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      {/* <section className="section-padding bg-dark text-white">
        <Container>
          <div className="text-center reveal-up">
            <h2 className="fw-black mb-4" style={{ fontSize: 'clamp(35px, 6vw, 65px)' }}>{t('getInTouch')}</h2>
            <p className="mb-10 opacity-70 mx-auto fs-5" style={{ maxWidth: '600px' }}>{t('contactDescription')}</p>
            <Button size="lg" className="px-10 py-3 rounded-1 font-black bg-white text-dark border-0 hover-scale" onClick={() => navigate('/#contact')}>
              {t('startYourJourney')}
            </Button>
          </div>
        </Container>
      </section> */}
    </div>
  );
};